import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

// vizShared — cohesive primitives for the ACE Core Chemistry interactive widgets.
// Provides: the optional narration `cue` contract, a reduced-motion hook, a
// one-shot self-demo sequencer (cancellable by interaction or an incoming cue),
// the standard card shell with a mount entrance animation, and a "Now you try"
// post-demo cue pill. All widgets import from here so the design stays uniform.
//
// NOTE on tokens: `--ws-dark` and `--ws-hairline` exist as CSS vars but are NOT
// registered as Tailwind colors, so `text-ws-dark` / `bg-ws-hairline` render no
// color. Use the inline helpers below (DARK_INK, etc.) for legible text on
// colored chips instead.

// Dark, legible ink for text sitting on a colored element/atom chip. Defined for
// every theme as the --ws-dark CSS var.
export const DARK_INK = 'var(--ws-dark)';
export const DARK_INK_SOFT = 'rgba(20,26,32,0.7)';

// ---------------------------------------------------------------------------
// Narration sync cue — optional prop on EVERY widget. Absent/empty ⇒ widget
// behaves exactly as it would standalone.
// ---------------------------------------------------------------------------
export interface VizCue {
  /** 0..1 timeline scrubber (drives whatever continuous quantity a widget has). */
  progress?: number;
  /** Symbol(s) / category / token to spotlight. */
  highlight?: string | string[];
  /** Discrete step index to advance to (balancer, stoichiometry, aufbau, …). */
  step?: number;
  /** Drive players (particle/titration/configuration …). */
  play?: boolean;
}

/** True when a cue actually carries instructions (so widgets can skip self-demo). */
export function cueIsActive(cue?: VizCue): boolean {
  if (!cue) return false;
  return (
    cue.progress != null ||
    cue.step != null ||
    cue.play != null ||
    (cue.highlight != null &&
      (Array.isArray(cue.highlight) ? cue.highlight.length > 0 : cue.highlight !== ''))
  );
}

/** Normalize cue.highlight to a lowercased string[] for easy membership tests. */
export function cueHighlights(cue?: VizCue): string[] {
  const h = cue?.highlight;
  if (h == null) return [];
  return (Array.isArray(h) ? h : [h]).map((s) => String(s).toLowerCase());
}

// ---------------------------------------------------------------------------
// Reduced motion — live (responds if the user changes the OS setting).
// ---------------------------------------------------------------------------
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setReduced(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return reduced;
}

// ---------------------------------------------------------------------------
// useMountSequence — runs an ordered list of timed steps ONCE on mount (the
// self-demo). Skips entirely when `skip` is true (reduced motion or a cue is
// driving). Any user interaction calls cancel(); cancel stops pending steps and
// (optionally) jumps to the natural resting state via onCancel.
//
//   const demo = useMountSequence(
//     [ { at: 300, run: () => setX(1) }, { at: 900, run: () => setX(2) } ],
//     { skip: reduced || hasCue, onSettle: () => setX(natural) },
//   );
//   // attach demo.cancel to first interaction; read demo.running / demo.done.
// ---------------------------------------------------------------------------
export interface MountStep {
  /** Delay in ms from mount when this step fires. */
  at: number;
  run: () => void;
}

export interface MountSequence {
  running: boolean;
  done: boolean;
  /** Stop the demo immediately and hand control to the learner. */
  cancel: () => void;
}

export function useMountSequence(
  steps: MountStep[],
  opts: { skip?: boolean; onSettle?: () => void } = {},
): MountSequence {
  const { skip = false } = opts;
  const [running, setRunning] = useState(!skip && steps.length > 0);
  const [done, setDone] = useState(skip || steps.length === 0);
  const timers = useRef<number[]>([]);
  // Keep latest callbacks without re-arming timers.
  const stepsRef = useRef(steps);
  stepsRef.current = steps;
  const settleRef = useRef(opts.onSettle);
  settleRef.current = opts.onSettle;
  const cancelledRef = useRef(false);

  const cancel = useCallback(() => {
    if (cancelledRef.current) return;
    cancelledRef.current = true;
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
    setRunning(false);
    setDone(true);
    settleRef.current?.();
  }, []);

  useEffect(() => {
    if (skip || stepsRef.current.length === 0) {
      setRunning(false);
      setDone(true);
      return;
    }
    cancelledRef.current = false;
    setRunning(true);
    setDone(false);
    const list = stepsRef.current;
    let last = 0;
    list.forEach((s) => {
      last = Math.max(last, s.at);
      const id = window.setTimeout(() => {
        if (!cancelledRef.current) s.run();
      }, s.at);
      timers.current.push(id);
    });
    // Mark done a tick after the last step.
    const endId = window.setTimeout(() => {
      if (!cancelledRef.current) {
        setRunning(false);
        setDone(true);
      }
    }, last + 60);
    timers.current.push(endId);
    return () => {
      timers.current.forEach((t) => window.clearTimeout(t));
      timers.current = [];
    };
    // Arm once on mount; `skip` is read at mount time only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { running, done, cancel };
}

// ---------------------------------------------------------------------------
// useMounted — flips true on the first frame after mount, for entrance
// transitions (opacity/translate driven by CSS transition, not @keyframes).
// ---------------------------------------------------------------------------
export function useMounted(): boolean {
  const [m, setM] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setM(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return m;
}

// ---------------------------------------------------------------------------
// VizCard — the standard premium card shell. Entrance: fade + slight rise on
// mount (skipped under reduced motion). Header supports an eyebrow (small caps),
// title, subtitle, a right-aligned actions slot, and a footer hint line.
// ---------------------------------------------------------------------------
export function VizCard({
  title,
  eyebrow,
  subtitle,
  hint,
  actions,
  reducedMotion = false,
  className = '',
  bodyClassName = '',
  children,
}: {
  title?: ReactNode;
  eyebrow?: ReactNode;
  subtitle?: ReactNode;
  hint?: ReactNode;
  actions?: ReactNode;
  reducedMotion?: boolean;
  className?: string;
  bodyClassName?: string;
  children: ReactNode;
}) {
  const mounted = useMounted();
  const entranceStyle: CSSProperties = reducedMotion
    ? {}
    : {
        opacity: mounted ? 1 : 0,
        transform: mounted ? 'translateY(0)' : 'translateY(6px)',
        transition: 'opacity 360ms ease-out, transform 360ms ease-out',
      };

  const hasHeader = title != null || eyebrow != null || subtitle != null || actions != null;

  return (
    <section
      style={entranceStyle}
      className={`w-full rounded-lg border border-ws-border bg-ws-surface p-3 shadow-sm sm:p-4 ${className}`}
    >
      {hasHeader && (
        <header className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            {eyebrow != null && (
              <div className="text-[10px] font-medium uppercase tracking-wide text-ws-ink-3">
                {eyebrow}
              </div>
            )}
            {title != null && (
              <h3 className="truncate text-sm font-semibold text-ws-ink">{title}</h3>
            )}
            {subtitle != null && (
              <p className="mt-0.5 text-xs text-ws-ink-3">{subtitle}</p>
            )}
          </div>
          {actions != null && <div className="flex flex-shrink-0 items-center gap-1.5">{actions}</div>}
        </header>
      )}
      <div className={bodyClassName}>{children}</div>
      {hint != null && (
        <p className="mt-3 text-[11px] text-ws-ink-3">{hint}</p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// NowYouTry — a small auto-fading "Now you try" pill, absolutely positioned in
// the top-right of a relative stage. Render after the self-demo settles; it
// fades itself out after `duration` ms. Skipped (returns null) when !show.
// ---------------------------------------------------------------------------
export function NowYouTry({
  show,
  label = 'Now you try',
  duration = 4200,
  className = '',
}: {
  show: boolean;
  label?: string;
  duration?: number;
  className?: string;
}) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!show) {
      setVisible(false);
      return;
    }
    setVisible(true);
    const id = window.setTimeout(() => setVisible(false), duration);
    return () => window.clearTimeout(id);
  }, [show, duration]);

  return (
    <span
      aria-hidden={!visible}
      className={`pointer-events-none absolute right-2 top-2 z-10 inline-flex items-center gap-1 rounded-md bg-ws-accent px-2.5 py-1 text-[11px] font-medium text-white shadow-sm ${className}`}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(-4px)',
        transition: 'opacity 300ms ease-out, transform 300ms ease-out',
      }}
    >
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-white" />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Shared control class strings — keep buttons tactile + accessible everywhere.
// Compose with template literals: `className={VIZ_BTN_PRIMARY}`.
// ---------------------------------------------------------------------------
export const VIZ_FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent focus-visible:ring-offset-1 focus-visible:ring-offset-ws-surface';

// NOTE: text/icons on the SATURATED accent must stay high-contrast. The
// `--ws-accent-ink` token is tuned for the PALE accent-quiet background and is
// dark in some themes (e.g. paper rust), so it reads poorly on the full accent.
// Use white here (and on every "selected"/active-on-accent toggle) instead.
export const VIZ_BTN_PRIMARY =
  `flex items-center gap-1.5 rounded-sm bg-ws-accent px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-ws-accent-hover active:brightness-95 disabled:cursor-not-allowed disabled:opacity-40 ${VIZ_FOCUS}`;

// Shared "selected/active toggle sitting on the accent fill" class. Compose with
// a conditional so all viz toggles share one high-contrast treatment.
export const VIZ_TOGGLE_SELECTED = 'bg-ws-accent text-white';

export const VIZ_BTN_GHOST =
  `flex items-center gap-1.5 rounded-sm border border-ws-border bg-ws-tile px-2.5 py-1.5 text-xs font-medium text-ws-ink-2 transition-colors hover:bg-ws-surface-2 active:brightness-95 disabled:cursor-not-allowed disabled:opacity-40 ${VIZ_FOCUS}`;

export const VIZ_BTN_ICON =
  `rounded-sm border border-ws-border p-1.5 text-ws-ink-2 transition-colors hover:bg-ws-surface-2 active:brightness-95 disabled:cursor-not-allowed disabled:opacity-40 ${VIZ_FOCUS}`;
